/**
 * Footer — CHI Alliance
 * Obsidian Pulse design: dark, clean, branded
 */
import { Link } from "wouter";
import { Phone, Mail, Instagram, Linkedin, Mic } from "lucide-react";
import CtaIcon from "@/components/CtaIcon";
import { useSpeakerApplication } from "@/contexts/SpeakerApplicationContext";
import { useOptionalAuth } from "@/contexts/AuthContext";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/mission-and-values", label: "Mission & Values" },
  { href: "/collaborate-with-us", label: "Collaborate with Us" },
  { href: "/featured-speakers", label: "Featured Speakers" },
  { href: "/calendar-of-events", label: "Calendar of Events" },
];

export default function Footer() {
  const { openSpeakerApplication } = useSpeakerApplication();
  const auth = useOptionalAuth();
  const isAdministrator = auth?.user?.role === "admin";

  return (
    <footer style={{ background: "#050810", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <Link href="/" className="flex items-center mb-4">
              <img
                src="/media/brand/chi-flower-symbol.png"
                alt="The Chi Alliance"
                style={{ width: 52, height: "auto", objectFit: "contain" }}
              />
            </Link>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.6rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#F9A8D4", marginBottom: "0.75rem" }}>
              Connecting Human Innovators
            </div>
            <p style={{ color: "#64748B", fontSize: "var(--chi-body-compact)", lineHeight: 1.7, fontFamily: "'Inter', sans-serif", maxWidth: "280px" }}>
              A movement dedicated to building authentic relationships, sharing wisdom, and elevating human connections.
            </p>
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.instagram.com/thechialliance/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{ background: "rgba(233,30,140,0.1)", border: "1px solid rgba(233,30,140,0.2)", color: "#E91E8C" }}
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://www.linkedin.com/company/feng-shui-journey-1/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{ background: "rgba(0,212,255,0.1)", border: "1px solid rgba(0,212,255,0.2)", color: "#00D4FF" }}
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: "#E91E8C", marginBottom: "1.25rem" }}>
              Quick Links
            </h4>
            <ul className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    style={{ color: "#94A3B8", fontSize: "0.875rem", fontFamily: "'Inter', sans-serif", transition: "color 0.2s" }}
                    className="hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  href="/privacy-policy"
                  style={{ color: "#94A3B8", fontSize: "0.875rem", fontFamily: "'Inter', sans-serif", transition: "color 0.2s" }}
                  className="hover:text-white"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  href="/disclaimer"
                  style={{ color: "#94A3B8", fontSize: "0.875rem", fontFamily: "'Inter', sans-serif", transition: "color 0.2s" }}
                  className="hover:text-white"
                >
                  Disclaimer
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: "#E91E8C", marginBottom: "1.25rem" }}>
              Contact
            </h4>
            <div className="flex flex-col gap-3">
              <a
                href="tel:+12065375222"
                className="flex items-center gap-3 hover:text-white transition-colors"
                style={{ color: "#94A3B8", fontSize: "0.875rem", fontFamily: "'Inter', sans-serif" }}
              >
                <Phone size={14} style={{ color: "#E91E8C" }} />
                (469) 919-4764
              </a>
              <a
                href="mailto:info@thechialliance.com"
                className="flex items-center gap-3 hover:text-white transition-colors"
                style={{ color: "#94A3B8", fontSize: "0.875rem", fontFamily: "'Inter', sans-serif" }}
              >
                <Mail size={14} style={{ color: "#E91E8C" }} />
                info@thechialliance.com
              </a>
            </div>
            {!isAdministrator && (
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => openSpeakerApplication("speaker")}
                  aria-haspopup="dialog"
                  className="chi-btn-primary inline-flex items-center gap-2"
                  style={{ fontSize: "0.75rem", padding: "0.625rem 1.5rem" }}
                >
                  Join The Chi Alliance <CtaIcon icon={Mic} size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="chi-divider mt-12 mb-6" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p style={{ color: "#475569", fontSize: "0.8rem", fontFamily: "'Space Grotesk', sans-serif" }}>
            © 2026 The CHI Alliance. All rights reserved.
          </p>
          <p style={{ color: "#94A3B8", fontSize: "0.8rem", fontFamily: "'Space Grotesk', sans-serif" }}>
            Connecting Human Innovators
          </p>
        </div>
      </div>
    </footer>
  );
}
